import React, { useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { readAuthUser } from "../../helpers/studioModules";
import notifications from "../../common/Components/Notifications/Notification";
import {
  recordPublicConsent,
  withdrawPublicConsent,
  type PublicConsent,
} from "../../services/artisanPortraitApi";
import "./ArtisanPublicConsent.scss";

export type ConsentAction = "show" | "stop" | "needPhoto" | "askAdmin" | null;

/** What the studio can do next about showing this artisan on certificates. */
export const consentAction = (status: PublicConsent["status"] | undefined, hasPhoto: boolean, canRecord: boolean): ConsentAction => {
  if (status === "GRANTED") return canRecord ? "stop" : null;
  if (!hasPhoto) return "needPhoto";
  return canRecord ? "show" : "askAdmin";
};

/** Only the studio admin and managers record or withdraw consent (the server checks too). */
export const canRecordConsent = (role?: string | null) => role === "ORGANIZATION_ADMIN" || role === "ORGANIZATION_MANAGER";

const MAX_REASON = 200;

type Props = {
  userId: string;
  name?: string;
  hasPhoto: boolean;
  consent?: PublicConsent;
  onChange: (userId: string, consent: PublicConsent) => void;
};

/**
 * Under an artisan's portrait: whether they are shown on this studio's certificates, and the one
 * click to record that they agreed in person (or to stop). The artisan needs no phone.
 */
const ArtisanPublicConsent = ({ userId, name, hasPhoto, consent, onChange }: Props) => {
  const canRecord = canRecordConsent(readAuthUser()?.users?.role);
  const action = consentAction(consent?.status, hasPhoto, canRecord);
  const [mode, setMode] = useState<"show" | "stop" | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [adult, setAdult] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const who = name?.trim() || "the artisan";
  // Agreed but no photo right now (removed): nothing appears publicly until a new portrait is taken.
  const agreedNoPhoto = consent?.status === "GRANTED" && !hasPhoto;
  const shown = consent?.status === "GRANTED" && hasPhoto;

  const open = (next: "show" | "stop") => {
    setAgreed(false);
    setAdult(false);
    setReason("");
    setMode(next);
  };
  const close = () => {
    if (!busy) setMode(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const next = mode === "show" ? await recordPublicConsent(userId) : await withdrawPublicConsent(userId, reason.trim());
      onChange(userId, next);
      notifications({
        title: mode === "show" ? "Shown on certificates" : "No longer shown",
        message: mode === "show" ? `${who}'s photo now appears on pieces they made.` : `${who}'s photo is off your certificates.`,
        type: "success",
      });
      setMode(null);
    } catch (e: any) {
      notifications({ title: "Not saved", message: e?.message || "Try again", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const ready = mode === "show" ? agreed && adult : reason.trim().length > 0;

  return (
    <div className="hkp-consent">
      <span className={`hkp-consent__chip${shown ? " is-on" : ""}`}>
        {shown ? "On certificates" : agreedNoPhoto ? "Agreed, no photo" : "Team only"}
      </span>
      {action === "show" ? (
        <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={() => open("show")}>
          Show on certificates
        </button>
      ) : null}
      {action === "stop" ? (
        <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => open("stop")}>
          Stop showing
        </button>
      ) : null}
      {action === "needPhoto" ? <small className="hkp-consent__hint">Take a portrait to show it on certificates</small> : null}
      {action === "askAdmin" ? <small className="hkp-consent__hint">Your studio admin can show it on certificates</small> : null}

      <Modal isOpen={mode !== null} toggle={close} centered fade={false} className="hkp-consent-modal">
        <ModalHeader toggle={close}>{mode === "show" ? "Show on certificates" : "Stop showing"}</ModalHeader>
        <ModalBody>
          {mode === "show" ? (
            <>
              <p className="hkp-consent-modal__lead">Read this to {who} and ask if they agree.</p>
              <blockquote className="hkp-consent-modal__say">
                <p>
                  Your photo, name and district will be shown on the certificates of pieces you made for us. Buyers
                  can see it. You can tell us to stop at any time.
                </p>
                <p lang="hi">
                  आपकी फ़ोटो, नाम और ज़िला उन चीज़ों के प्रमाणपत्र पर दिखेंगे जो आपने हमारे लिए बनाई हैं। ख़रीदार इसे देख सकते हैं। आप
                  कभी भी मना कर सकते हैं।
                </p>
              </blockquote>
              <label className="hkp-consent-modal__tick">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                <span>{who} agreed</span>
              </label>
              <label className="hkp-consent-modal__tick">
                <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} />
                <span>{who} is 18 or over</span>
              </label>
            </>
          ) : (
            <>
              <p className="hkp-consent-modal__lead">
                {who}'s photo comes off your certificates now. Their name stays on the pieces.
              </p>
              <label className="hkp-consent-modal__field" htmlFor={`consent-reason-${userId}`}>
                Why
              </label>
              <input
                id={`consent-reason-${userId}`}
                className="hkp-consent-modal__input"
                maxLength={MAX_REASON}
                placeholder="For example: the artisan asked"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button color={mode === "stop" ? "danger" : "primary"} onClick={() => void save()} disabled={!ready || busy}>
            {busy ? "Saving…" : mode === "show" ? "Save" : "Stop showing"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ArtisanPublicConsent;
